import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import AssetCard from "../components/AssetCard";
import { api } from "../lib/api";
import { CATEGORY_LIST } from "../lib/i18n";
import { Search } from "lucide-react";

export default function Browse() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "recent";

  useEffect(() => {
    setLoading(true);
    api
      .get("/files", {
        params: {
          category: category === "all" ? undefined : category,
          q: params.get("q") || undefined,
          sort,
          limit: 48,
        },
      })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [params, category, sort]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v);
    else np.delete(k);
    setParams(np, { replace: true });
  };

  const onSearch = (e) => {
    e.preventDefault();
    setParam("q", q.trim());
  };

  const catName = (id) => {
    const map = {
      all: t("allAssets"), powerpoint: t("powerpoint"), word: t("word"), excel: t("excel"),
      images: t("images"), "3d-icons": t("threeDIcons"), icons: t("icons"), animation: t("animation"),
      stickers: t("stickers"), cv: t("cv"), school: t("school"), video: t("video"), other: t("other"),
    };
    return map[id] || id;
  };

  return (
    <main className="min-h-[calc(100vh-72px)] py-14">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">§ — Library</div>
            <h1 className="font-display mt-3 text-5xl leading-[0.95] sm:text-6xl" data-testid="browse-title">
              <span className="text-ink">All the</span>{" "}
              <span className="font-serif text-accent">assets.</span>
            </h1>
          </div>
          <form onSubmit={onSearch} className="flex items-center gap-2 lg:justify-end">
            <div className="relative flex-1 lg:max-w-sm">
              <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="input-base ps-10"
                data-testid="browse-search-input"
              />
            </div>
            <button type="submit" className="btn btn-primary" data-testid="browse-search-btn">
              {t("search")}
            </button>
          </form>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setParam("category", "")}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] ${
              category === "all"
                ? "border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                : "text-[hsl(var(--ink-2))] hover:bg-[hsl(var(--surface-2))]"
            }`}
            style={{ borderColor: category === "all" ? "hsl(var(--ink))" : "hsl(var(--border))" }}
            data-testid="browse-cat-all"
          >
            {t("allAssets")}
          </button>
          {CATEGORY_LIST.map((c) => (
            <button
              key={c.id}
              onClick={() => setParam("category", c.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] ${
                category === c.id
                  ? "border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                  : "text-[hsl(var(--ink-2))] hover:bg-[hsl(var(--surface-2))]"
              }`}
              style={{ borderColor: category === c.id ? "hsl(var(--ink))" : "hsl(var(--border))" }}
              data-testid={`browse-cat-${c.id}`}
            >
              {catName(c.id)}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted-fg">
          <span>Sort:</span>
          <button
            onClick={() => setParam("sort", "recent")}
            className={`rounded-full px-3 py-1 ${sort === "recent" ? "bg-accent text-[hsl(var(--accent-fg))]" : "hover:bg-[hsl(var(--surface-2))]"}`}
            data-testid="browse-sort-recent"
          >
            {t("recent")}
          </button>
          <button
            onClick={() => setParam("sort", "popular")}
            className={`rounded-full px-3 py-1 ${sort === "popular" ? "bg-accent text-[hsl(var(--accent-fg))]" : "hover:bg-[hsl(var(--surface-2))]"}`}
            data-testid="browse-sort-popular"
          >
            {t("popular")}
          </button>
        </div>

        {loading ? (
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-[var(--radius)] surface-2" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-12 card p-16 text-center">
            <div className="font-display text-3xl text-muted-fg">{t("noResults")}</div>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((it) => (
              <AssetCard key={it.id} item={it} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
